(() => {
  "use strict";

  const qs = (root, selector) => root.querySelector(selector);
  const qsa = (root, selector) => Array.from(root.querySelectorAll(selector));
  const esc = (value) => String(value ?? "")
    .replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;").replaceAll("'", "&#039;");
  const clamp = (n, min, max) => Math.max(min, Math.min(max, Number(n) || 0));
  const normalise = (value) => String(value || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  const uuid = () => (crypto?.randomUUID ? crypto.randomUUID() :
    "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      const v = c === "x" ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    }));

  class FanScoreEnglish {
    constructor(root) {
      this.root = root;
      this.url = String(root.dataset.supabaseUrl || "").replace(/\/$/, "");
      this.key = root.dataset.publishableKey || "";
      this.seasonKey = root.dataset.seasonKey || "2026-27";
      this.defaultClubSlug = root.dataset.defaultClub || "arsenal";
      this.directory = [];
      this.catalog = [];
      this.rollups = [];
      this.club = null;
      this.currentMatchup = null;
      this.session = this.readSession();
      this.gameCleanup = null;
      this.levelLabels = {
        1: "Premier League",
        2: "EFL Championship",
        3: "EFL League One",
        4: "EFL League Two",
        5: "National League",
        6: "National League North & South",
        7: "Step 3",
        8: "Step 4",
        9: "Step 5",
        10: "Step 6"
      };
    }

    async init() {
      this.bindStaticEvents();
      this.setLoadingState();
      try {
        const [directory, catalog] = await Promise.all([
          this.fetchPaged("fse_club_directory", {
            select: "id,source_key,slug,name,short_name,crest_url,primary_color,secondary_color,accent_color,town,region,ground_name,season_key,competition_id,competition_slug,competition_name,league_name,system_level,fa_step",
            season_key: `eq.${this.seasonKey}`,
            order: "system_level.asc,name.asc"
          }),
          this.api("fse_game_catalog", {
            query: {
              select: "game_key,name,subtitle,description,sort_order,active",
              active: "eq.true",
              order: "sort_order.asc"
            }
          })
        ]);
        this.directory = directory || [];
        this.catalog = catalog || [];
        this.renderPyramid();
        this.renderDirectory();
        this.renderClubResults();
        this.populateLevelFilter();

        const saved = localStorage.getItem("fanscore_english_club_id");
        const initial = this.directory.find(c => c.id === saved)
          || this.directory.find(c => c.slug === this.defaultClubSlug)
          || this.directory[0];

        if (initial) await this.selectClub(initial, { persistProfile: false });
      } catch (error) {
        console.error("[FanScore English] init failed", error);
        this.renderFatal(error);
      }
    }

    bindStaticEvents() {
      qsa(this.root, "[data-fse-open-club]").forEach(btn => {
        btn.addEventListener("click", () => this.openClubDialog());
      });

      const clubDialog = qs(this.root, "[data-fse-club-dialog]");
      const clubSearch = qs(this.root, "[data-fse-club-search]");
      const levelFilter = qs(this.root, "[data-fse-level-filter]");
      clubSearch?.addEventListener("input", () => this.renderClubResults());
      levelFilter?.addEventListener("change", () => this.renderClubResults());

      const directorySearch = qs(this.root, "[data-fse-directory-search]");
      directorySearch?.addEventListener("input", () => this.renderDirectory());

      qsa(this.root, "[data-fse-game]").forEach(button => {
        button.addEventListener("click", () => this.openGame(button.dataset.fseGame));
      });

      qs(this.root, "[data-fse-game-close]")?.addEventListener("click", () => this.closeGame());
      const gameDialog = qs(this.root, "[data-fse-game-dialog]");
      gameDialog?.addEventListener("close", () => this.cleanupGame());

      clubDialog?.addEventListener("click", event => {
        if (event.target === clubDialog) clubDialog.close();
      });
      gameDialog?.addEventListener("click", event => {
        if (event.target === gameDialog) this.closeGame();
      });
    }

    readSession() {
      try {
        for (const key of Object.keys(localStorage)) {
          if (!key.startsWith("sb-") || !key.endsWith("-auth-token")) continue;
          const value = JSON.parse(localStorage.getItem(key));
          const candidate = Array.isArray(value) ? value[0] : value;
          if (candidate?.access_token && candidate?.user?.id) return candidate;
          if (candidate?.currentSession?.access_token && candidate?.currentSession?.user?.id) return candidate.currentSession;
        }
      } catch (error) {
        console.warn("[FanScore English] could not read auth session", error);
      }
      return null;
    }

    authHeaders(useAuth = false) {
      const headers = {
        "apikey": this.key,
        "Accept": "application/json"
      };
      if (useAuth && this.session?.access_token) {
        headers.Authorization = `Bearer ${this.session.access_token}`;
      }
      return headers;
    }

    async api(table, { method = "GET", query = {}, body = null, auth = false, headers = {} } = {}) {
      if (!this.url || !this.key) throw new Error("FanScore English is missing its Supabase configuration.");
      const params = new URLSearchParams();
      Object.entries(query || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") params.set(key, value);
      });
      const endpoint = `${this.url}/rest/v1/${table}${params.size ? `?${params.toString()}` : ""}`;
      const response = await fetch(endpoint, {
        method,
        headers: {
          ...this.authHeaders(auth),
          ...(body ? { "Content-Type": "application/json" } : {}),
          ...headers
        },
        body: body ? JSON.stringify(body) : undefined
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`${response.status}: ${text || response.statusText}`);
      }
      if (response.status === 204) return null;
      const text = await response.text();
      return text ? JSON.parse(text) : null;
    }

    async fetchPaged(table, query, pageSize = 1000) {
      const output = [];
      for (let offset = 0; offset < 10000; offset += pageSize) {
        const rows = await this.api(table, {
          query: { ...query, limit: String(pageSize), offset: String(offset) }
        });
        output.push(...(rows || []));
        if (!rows || rows.length < pageSize) break;
      }
      return output;
    }

    setLoadingState() {
      const list = qs(this.root, "[data-fse-directory-list]");
      if (list) list.innerHTML = '<div class="fse__loading">Loading the English football pyramid…</div>';
    }

    renderFatal(error) {
      const list = qs(this.root, "[data-fse-directory-list]");
      const results = qs(this.root, "[data-fse-club-results]");
      const message = `<div class="fse__empty">The English Football data layer could not load.<br><small>${esc(error?.message || "Unknown error")}</small></div>`;
      if (list) list.innerHTML = message;
      if (results) results.innerHTML = message;
    }

    populateLevelFilter() {
      const select = qs(this.root, "[data-fse-level-filter]");
      if (!select) return;
      const levels = [...new Set(this.directory.map(c => Number(c.system_level)))].sort((a, b) => a - b);
      select.innerHTML = '<option value="">All levels</option>' + levels.map(level => (
        `<option value="${level}">Level ${level} · ${esc(this.levelLabels[level] || `Level ${level}`)}</option>`
      )).join("");
    }

    openClubDialog() {
      const dialog = qs(this.root, "[data-fse-club-dialog]");
      if (!dialog) return;
      this.renderClubResults();
      if (dialog.showModal) dialog.showModal();
      else dialog.setAttribute("open", "");
      setTimeout(() => qs(this.root, "[data-fse-club-search]")?.focus(), 30);
    }

    closeClubDialog() {
      const dialog = qs(this.root, "[data-fse-club-dialog]");
      if (!dialog) return;
      if (dialog.close) dialog.close();
      else dialog.removeAttribute("open");
    }

    filterDirectory(term = "", level = "") {
      const needle = normalise(term);
      return this.directory.filter(c => {
        const haystack = normalise(`${c.name} ${c.competition_name} ${c.league_name} ${c.town || ""}`);
        const levelMatch = !level || String(c.system_level) === String(level);
        return levelMatch && (!needle || haystack.includes(needle));
      });
    }

    renderClubResults() {
      const host = qs(this.root, "[data-fse-club-results]");
      if (!host) return;
      const term = qs(this.root, "[data-fse-club-search]")?.value || "";
      const level = qs(this.root, "[data-fse-level-filter]")?.value || "";
      const rows = this.filterDirectory(term, level).slice(0, 120);

      if (!rows.length) {
        host.innerHTML = '<div class="fse__empty">No clubs match that search.</div>';
        return;
      }

      host.innerHTML = rows.map(c => `
        <button type="button" class="fse__club-result" data-club-id="${esc(c.id)}">
          <span><strong>${esc(c.name)}</strong><br><small>${esc(c.competition_name)}</small></span>
          <small>Level ${esc(c.system_level)}</small>
        </button>
      `).join("");

      qsa(host, "[data-club-id]").forEach(button => {
        button.addEventListener("click", async () => {
          const club = this.directory.find(c => c.id === button.dataset.clubId);
          if (club) {
            await this.selectClub(club, { persistProfile: true });
            this.closeClubDialog();
          }
        });
      });
    }

    renderDirectory(levelOverride = null) {
      const host = qs(this.root, "[data-fse-directory-list]");
      if (!host) return;
      const search = qs(this.root, "[data-fse-directory-search]")?.value || "";
      const rows = this.filterDirectory(search, levelOverride || "").slice(0, 140);
      const count = qs(this.root, "[data-fse-directory-count]");
      if (count) count.textContent = `${this.directory.length.toLocaleString("en-GB")} clubs`;

      host.innerHTML = rows.length ? rows.map(c => `
        <button type="button" class="fse__directory-item" data-club-id="${esc(c.id)}">
          <span><strong>${esc(c.name)}</strong><br><small>${esc(c.competition_name)}</small></span>
          <small>L${esc(c.system_level)}</small>
        </button>
      `).join("") : '<div class="fse__empty">No clubs match that search.</div>';

      qsa(host, "[data-club-id]").forEach(button => {
        button.addEventListener("click", async () => {
          const club = this.directory.find(c => c.id === button.dataset.clubId);
          if (club) await this.selectClub(club, { persistProfile: true });
        });
      });
    }

    renderPyramid() {
      const host = qs(this.root, "[data-fse-pyramid]");
      if (!host) return;
      const counts = this.directory.reduce((acc, club) => {
        acc[club.system_level] = (acc[club.system_level] || 0) + 1;
        return acc;
      }, {});
      host.innerHTML = Object.keys(counts).map(Number).sort((a, b) => a - b).map(level => {
        const narrow = Math.min((level - 1) * 14, 112);
        return `
          <button type="button" class="fse__pyramid-row" style="--narrow:${narrow}px" data-level="${level}">
            <span class="fse__pyramid-level">L${level}</span>
            <strong>${esc(this.levelLabels[level] || `Level ${level}`)}</strong>
            <small>${counts[level].toLocaleString("en-GB")} clubs</small>
          </button>
        `;
      }).join("");

      qsa(host, "[data-level]").forEach(button => {
        button.addEventListener("click", () => {
          const level = button.dataset.level;
          const directorySearch = qs(this.root, "[data-fse-directory-search]");
          if (directorySearch) directorySearch.value = "";
          this.renderDirectory(level);
          qs(this.root, "[data-fse-directory-list]")?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        });
      });
    }

    async selectClub(club, { persistProfile = true } = {}) {
      this.club = club;
      localStorage.setItem("fanscore_english_club_id", club.id);
      this.applyClubBranding(club);
      this.renderClubCard(club);
      this.renderDirectory();

      const action = qs(this.root, "[data-fse-club-action]");
      if (action) action.textContent = club.name;

      if (persistProfile && this.session?.user?.id) {
        try { await this.persistSupporterClub(club.id); }
        catch (error) { console.warn("[FanScore English] supporter profile update failed", error); }
      }

      await Promise.allSettled([
        this.loadNextMatchup(),
        this.loadPersonalStats(),
        this.loadLeaderboard()
      ]);
    }

    applyClubBranding(club) {
      if (club.primary_color) this.root.style.setProperty("--fse-club-primary", club.primary_color);
      if (club.secondary_color) this.root.style.setProperty("--fse-club-secondary", club.secondary_color);
      if (club.accent_color) this.root.style.setProperty("--fse-club-accent", club.accent_color);
    }

    initials(name) {
      const parts = String(name || "FS").replace(/\b(AFC|FC|United|Town|City|Rovers|Athletic)\b/gi, "").trim().split(/\s+/).filter(Boolean);
      return (parts.slice(0, 2).map(p => p[0]).join("") || "FS").toUpperCase();
    }

    crestHTML(club, fallback = null) {
      return club?.crest_url
        ? `<img src="${esc(club.crest_url)}" alt="">`
        : esc(fallback || this.initials(club?.name));
    }

    renderClubCard(club) {
      const crest = qs(this.root, "[data-fse-crest]");
      if (crest) crest.innerHTML = this.crestHTML(club);
      const name = qs(this.root, "[data-fse-club-name]");
      if (name) name.textContent = club.name;
      const division = qs(this.root, "[data-fse-division]");
      if (division) division.textContent = club.competition_name;
      const ground = qs(this.root, "[data-fse-ground]");
      if (ground) ground.textContent = club.ground_name || club.town || "English Football";
      const level = qs(this.root, "[data-fse-level]");
      if (level) level.textContent = `Level ${club.system_level} · ${this.levelLabels[club.system_level] || club.league_name}`;
    }

    async persistSupporterClub(clubId) {
      await this.api("fse_supporter_profiles", {
        method: "POST",
        auth: true,
        query: { on_conflict: "user_id" },
        headers: { Prefer: "resolution=merge-duplicates,return=minimal" },
        body: {
          user_id: this.session.user.id,
          club_id: clubId,
          display_name: this.session.user.user_metadata?.full_name || this.session.user.email?.split("@")[0] || "Supporter"
        }
      });
    }

    async loadNextMatchup() {
      if (!this.club) return;
      const nowMinus = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
      const rows = await this.api("fse_matchup_display", {
        query: {
          select: "*",
          or: `(home_club_id.eq.${this.club.id},away_club_id.eq.${this.club.id})`,
          kickoff_at: `gte.${nowMinus}`,
          order: "kickoff_at.asc",
          limit: "1"
        }
      });
      this.currentMatchup = rows?.[0] || null;
      this.renderMatchup();
    }

    renderMatchup() {
      const m = this.currentMatchup;
      if (!m) {
        const kicker = qs(this.root, "[data-fse-matchup-kicker]");
        if (kicker) kicker.textContent = `${this.club?.name || "Club"} · FIXTURE FEED READY`;
        qs(this.root, "[data-fse-home]")?.replaceChildren(document.createTextNode(this.club?.name || "Your Club"));
        qs(this.root, "[data-fse-away]")?.replaceChildren(document.createTextNode("Next opponent"));
        qs(this.root, "[data-fse-kickoff]")?.replaceChildren(document.createTextNode("Awaiting fixture"));
        qs(this.root, "[data-fse-venue]")?.replaceChildren(document.createTextNode(this.club?.competition_name || "English Football"));
        qs(this.root, "[data-fse-matchup-status]")?.replaceChildren(document.createTextNode("Fixtures create supporter matchups automatically when an authorised feed or CSV is imported."));
        return;
      }

      const homeClub = this.directory.find(c => c.id === m.home_club_id) || { name: m.home_club, crest_url: m.home_crest_url };
      const awayClub = this.directory.find(c => c.id === m.away_club_id) || { name: m.away_club, crest_url: m.away_crest_url };
      qs(this.root, "[data-fse-matchup-kicker]").textContent = `${this.club.competition_name} · SUPPORTER MATCHUP`;
      qs(this.root, "[data-fse-home]").textContent = m.home_club;
      qs(this.root, "[data-fse-away]").textContent = m.away_club;
      qs(this.root, "[data-fse-home-crest]").innerHTML = this.crestHTML(homeClub, "H");
      qs(this.root, "[data-fse-away-crest]").innerHTML = this.crestHTML(awayClub, "A");
      qs(this.root, "[data-fse-home-score]").textContent = Number(m.home_fanscore || 0).toFixed(1);
      qs(this.root, "[data-fse-away-score]").textContent = Number(m.away_fanscore || 0).toFixed(1);
      qs(this.root, "[data-fse-kickoff]").textContent = m.kickoff_at ? new Intl.DateTimeFormat("en-GB", {
        weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/London"
      }).format(new Date(m.kickoff_at)) : "Kickoff TBC";
      qs(this.root, "[data-fse-venue]").textContent = m.venue_name || "Venue TBC";
      qs(this.root, "[data-fse-matchup-status]").textContent =
        m.status === "open" ? "The supporter matchup is open. Every eligible FanScore point helps."
        : m.status === "closed" ? "The supporter matchup is closed."
        : "This supporter matchup opens 72 hours before kickoff.";
    }

    async loadPersonalStats() {
      const seasonScore = qs(this.root, "[data-fse-season-score]");
      const matches = qs(this.root, "[data-fse-matches]");
      const miles = qs(this.root, "[data-fse-miles]");
      if (!this.session?.user?.id || !this.club) {
        if (seasonScore) seasonScore.textContent = "Sign in";
        if (matches) matches.textContent = "—";
        if (miles) miles.textContent = "—";
        return;
      }

      const rows = await this.api("fse_user_rollups", {
        auth: true,
        query: {
          select: "points,matches_logged,miles_travelled",
          user_id: `eq.${this.session.user.id}`,
          club_id: `eq.${this.club.id}`,
          period_key: `eq.season:${this.seasonKey}`,
          limit: "1"
        }
      });
      const stat = rows?.[0];
      if (seasonScore) seasonScore.textContent = Number(stat?.points || 0).toLocaleString("en-GB");
      if (matches) matches.textContent = Number(stat?.matches_logged || 0).toLocaleString("en-GB");
      if (miles) miles.textContent = Math.round(Number(stat?.miles_travelled || 0)).toLocaleString("en-GB");
    }

    async loadLeaderboard() {
      const rows = await this.fetchPaged("fse_club_rollups", {
        select: "club_id,points,unique_fans,plays_count",
        period_key: `eq.season:${this.seasonKey}`,
        order: "points.desc"
      });
      this.rollups = rows || [];
      const rank = this.rollups.findIndex(r => r.club_id === this.club?.id);
      const rankEl = qs(this.root, "[data-fse-rank]");
      if (rankEl) rankEl.textContent = rank >= 0 ? `#${rank + 1}` : "—";

      const host = qs(this.root, "[data-fse-leaderboard]");
      if (!host) return;
      if (!this.rollups.length) {
        host.innerHTML = '<tr><td colspan="5">The fanbase table will populate as supporters score.</td></tr>';
        return;
      }

      const clubMap = new Map(this.directory.map(c => [c.id, c]));
      host.innerHTML = this.rollups.slice(0, 15).map((r, index) => {
        const club = clubMap.get(r.club_id);
        return `<tr>
          <td>${index + 1}</td>
          <td>${esc(club?.name || "Club")}</td>
          <td>${Number(r.points || 0).toLocaleString("en-GB")}</td>
          <td>${Number(r.unique_fans || 0).toLocaleString("en-GB")}</td>
          <td>${Number(r.plays_count || 0).toLocaleString("en-GB")}</td>
        </tr>`;
      }).join("");
    }

    cleanupGame() {
      if (typeof this.gameCleanup === "function") {
        try { this.gameCleanup(); } catch (_) {}
      }
      this.gameCleanup = null;
      const stage = qs(this.root, "[data-fse-game-stage]");
      if (stage) stage.innerHTML = "";
    }

    closeGame() {
      this.cleanupGame();
      const dialog = qs(this.root, "[data-fse-game-dialog]");
      if (!dialog) return;
      if (dialog.close) dialog.close();
      else dialog.removeAttribute("open");
    }

    openGame(key) {
      if (!this.club) {
        this.openClubDialog();
        return;
      }
      this.cleanupGame();
      const game = this.catalog.find(g => g.game_key === key) || { game_key: key, name: key, subtitle: "" };
      const dialog = qs(this.root, "[data-fse-game-dialog]");
      qs(this.root, "[data-fse-game-title]").textContent = game.name || key;
      qs(this.root, "[data-fse-game-eyebrow]").textContent = `${this.club.name} · GAME CENTRE`;
      if (dialog?.showModal) dialog.showModal();
      else dialog?.setAttribute("open", "");

      const handlers = {
        "golden-boot": () => this.gameGoldenBoot(),
        "top-bins": () => this.gameTopBins(),
        "club-quiz": () => this.gameClubQuiz(),
        "match-predictor": () => this.gamePredictor(),
        "the-big-list": () => this.gameBigList(),
        "falling-footballs": () => this.gameFallingFootballs(),
        "long-throw": () => this.gameLongThrow(),
        "match-log": () => this.gameMatchLog()
      };
      (handlers[key] || (() => this.gameUnavailable(key)))();
    }

    stage(html) {
      const stage = qs(this.root, "[data-fse-game-stage]");
      if (stage) stage.innerHTML = html;
      return stage;
    }

    intro(title, body) {
      return `<div class="fse__game-intro"><h3>${esc(title)}</h3><p>${esc(body)}</p></div>`;
    }

    async finishGame(key, rawScore, result = {}) {
      const stage = qs(this.root, "[data-fse-game-stage]");
      const rounded = Math.max(0, Math.round(rawScore || 0));
      let award = null;
      let note = "Practice score only. Sign in to bank FanScore points.";

      if (this.session?.user?.id && this.club) {
        try {
          const rows = await this.api("fse_game_sessions", {
            method: "POST",
            auth: true,
            headers: { Prefer: "return=representation" },
            body: {
              client_session_id: uuid(),
              user_id: this.session.user.id,
              club_id: this.club.id,
              fixture_id: this.currentMatchup?.fixture_id || null,
              game_key: key,
              raw_score: rounded,
              result,
              metadata: { client: "fanscore-english-home-v1" },
              completed_at: new Date().toISOString()
            }
          });
          award = rows?.[0];
          note = award?.awarded
            ? `+${Number(award.fanscore_points || 0).toLocaleString("en-GB")} FanScore points banked.`
            : "Score recorded. Today’s scoring award for this game has already been used.";
          await Promise.allSettled([this.loadPersonalStats(), this.loadLeaderboard(), this.loadNextMatchup()]);
        } catch (error) {
          console.error("[FanScore English] score submit failed", error);
          note = "Your game finished, but the score could not be banked.";
        }
      }

      stage.innerHTML = `<div class="fse__result">
        <span class="fse__smallcaps">FINAL SCORE</span>
        <strong>${rounded.toLocaleString("en-GB")}</strong>
        <p>${esc(note)}</p>
        <div class="fse__game-actions" style="justify-content:center">
          <button type="button" class="fse__button fse__button--primary" data-play-again>Play again</button>
          <button type="button" class="fse__button fse__button--quiet" data-close-result>Back to FanScore</button>
        </div>
      </div>`;

      qs(stage, "[data-play-again]")?.addEventListener("click", () => this.openGame(key));
      qs(stage, "[data-close-result]")?.addEventListener("click", () => this.closeGame());
    }

    gameGoldenBoot() {
      const stage = this.stage(`
        ${this.intro("Five finishes. One sweet spot.", "Stop the moving marker inside the scoring zone. Your five attempts are added together.")}
        <div class="fse__game-panel">
          <div class="fse__smallcaps">ATTEMPT <span data-attempt>1</span> / 5</div>
          <div class="fse__game-score" data-score>0</div>
          <div class="fse__meter"><div class="fse__meter-zone"></div><div class="fse__meter-needle" data-needle></div></div>
          <button type="button" class="fse__button fse__button--primary" data-shoot>Strike</button>
          <div class="fse__notice" data-note>Time it into the burgundy zone.</div>
        </div>
      `);
      let attempt = 1, total = 0, running = true, start = performance.now(), raf = 0;
      const needle = qs(stage, "[data-needle]");
      const loop = now => {
        const phase = ((now - start) % 1500) / 1500;
        const pos = phase < .5 ? phase * 200 : (1 - phase) * 200;
        needle.style.left = `${clamp(pos, 0, 100)}%`;
        if (running) raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);

      qs(stage, "[data-shoot]").addEventListener("click", () => {
        const pos = parseFloat(needle.style.left) || 0;
        const accuracy = Math.max(0, Math.round(100 - Math.abs(pos - 80) * 4.2));
        total += accuracy;
        qs(stage, "[data-score]").textContent = total;
        qs(stage, "[data-note]").textContent = accuracy >= 85 ? "Top corner." : accuracy >= 55 ? "On target." : "Dragged wide.";
        attempt += 1;
        if (attempt > 5) {
          running = false;
          cancelAnimationFrame(raf);
          setTimeout(() => this.finishGame("golden-boot", total, { attempts: 5 }), 450);
        } else {
          qs(stage, "[data-attempt]").textContent = attempt;
          start = performance.now();
        }
      });
      this.gameCleanup = () => { running = false; cancelAnimationFrame(raf); };
    }

    gameTopBins() {
      const stage = this.stage(`
        ${this.intro("Read it. Hit it.", "The called corner is outlined. Five shots. Exact hits score 100; adjacent zones still earn something.")}
        <div class="fse__game-panel">
          <div class="fse__smallcaps">SHOT <span data-attempt>1</span> / 5 · SCORE <span data-score>0</span></div>
          <p style="margin:12px 0!important;font-family:Georgia,serif;font-size:1.2rem">Called target: <strong data-call></strong></p>
          <div class="fse__target-grid" data-grid></div>
        </div>
      `);
      const names = ["Top left","Top centre","Top right","Middle left","Centre","Middle right","Bottom left","Bottom centre","Bottom right"];
      let attempt = 1, total = 0, called = Math.floor(Math.random() * 9);
      const grid = qs(stage, "[data-grid]");

      const draw = () => {
        qs(stage, "[data-call]").textContent = names[called];
        grid.innerHTML = names.map((name, i) => `<button type="button" class="fse__target" data-target="${i}" data-called="${i === called}">${i === called ? "×" : ""}</button>`).join("");
        qsa(grid, "[data-target]").forEach(button => button.addEventListener("click", () => {
          const picked = Number(button.dataset.target);
          const r1 = Math.floor(picked / 3), c1 = picked % 3;
          const r2 = Math.floor(called / 3), c2 = called % 3;
          const dist = Math.abs(r1 - r2) + Math.abs(c1 - c2);
          total += dist === 0 ? 100 : dist === 1 ? 45 : 10;
          qs(stage, "[data-score]").textContent = total;
          attempt += 1;
          if (attempt > 5) return this.finishGame("top-bins", total, { attempts: 5 });
          qs(stage, "[data-attempt]").textContent = attempt;
          called = Math.floor(Math.random() * 9);
          draw();
        }));
      };
      draw();
    }

    gameClubQuiz() {
      const divisionClubs = this.directory.filter(c => c.competition_id === this.club.competition_id);
      const outside = this.directory.filter(c => c.competition_id !== this.club.competition_id);
      const stage = this.stage(`
        ${this.intro("Know the division.", `Five quick questions built around ${this.club.competition_name}.`)}
        <div class="fse__game-panel">
          <div class="fse__smallcaps">QUESTION <span data-q>1</span> / 5 · SCORE <span data-score>0</span></div>
          <h3 style="font-size:1.7rem;margin-top:18px" data-question></h3>
          <div class="fse__quiz-options" data-options></div>
        </div>
      `);
      let q = 1, total = 0;
      const pick = arr => arr[Math.floor(Math.random() * arr.length)];
      const shuffle = arr => arr.map(v => [Math.random(), v]).sort((a,b) => a[0]-b[0]).map(v => v[1]);

      const next = () => {
        const answer = pick(divisionClubs);
        const distractors = [];
        while (distractors.length < 3 && outside.length) {
          const candidate = pick(outside);
          if (!distractors.some(x => x.id === candidate.id)) distractors.push(candidate);
        }
        const options = shuffle([answer, ...distractors]);
        qs(stage, "[data-question]").textContent = `Which club plays in ${this.club.competition_name}?`;
        qs(stage, "[data-options]").innerHTML = options.map(o => `<button type="button" class="fse__quiz-option" data-id="${esc(o.id)}">${esc(o.name)}</button>`).join("");
        qsa(stage, "[data-id]").forEach(button => button.addEventListener("click", () => {
          if (button.dataset.id === answer.id) total += 100;
          qs(stage, "[data-score]").textContent = total;
          q += 1;
          if (q > 5) return this.finishGame("club-quiz", total, { questions: 5 });
          qs(stage, "[data-q]").textContent = q;
          next();
        }));
      };
      next();
    }

    gamePredictor() {
      const m = this.currentMatchup;
      if (!m) {
        this.stage(`${this.intro("Match Predictor", "Predictions unlock when the club has an upcoming fixture in the feed.")}
          <div class="fse__empty">No upcoming fixture is loaded for ${esc(this.club.name)} yet.</div>`);
        return;
      }
      const stage = this.stage(`
        ${this.intro("Call the score.", "Predictions lock at kickoff. Exact score beats correct result.")}
        <div class="fse__game-panel">
          <div class="fse__score-inputs">
            <div class="fse__field"><label>${esc(m.home_club)}</label><input class="fse__input" type="number" min="0" max="20" value="1" data-home-pick></div>
            <div class="fse__field"><label>${esc(m.away_club)}</label><input class="fse__input" type="number" min="0" max="20" value="1" data-away-pick></div>
          </div>
          <div class="fse__game-actions"><button type="button" class="fse__button fse__button--primary" data-save>Lock prediction</button></div>
          <div class="fse__notice">Correct result: 150 points · exact score: 300 points. Points settle after the final result.</div>
        </div>
      `);
      qs(stage, "[data-save]").addEventListener("click", async () => {
        const home = clamp(qs(stage, "[data-home-pick]").value, 0, 20);
        const away = clamp(qs(stage, "[data-away-pick]").value, 0, 20);
        if (!this.session?.user?.id) {
          stage.innerHTML = `<div class="fse__result"><strong>${home}–${away}</strong><p>Prediction preview saved locally only. Sign in to enter it.</p></div>`;
          return;
        }
        try {
          await this.api("fse_predictions", {
            method: "POST",
            auth: true,
            query: { on_conflict: "user_id,fixture_id" },
            headers: { Prefer: "resolution=merge-duplicates,return=minimal" },
            body: {
              user_id: this.session.user.id,
              club_id: this.club.id,
              fixture_id: m.fixture_id,
              home_score: home,
              away_score: away,
              updated_at: new Date().toISOString()
            }
          });
          stage.innerHTML = `<div class="fse__result"><strong>${home}–${away}</strong><p>Prediction locked. FanScore settles when the fixture goes final.</p><div class="fse__game-actions" style="justify-content:center"><button type="button" class="fse__button fse__button--quiet" data-close>Back to FanScore</button></div></div>`;
          qs(stage, "[data-close]")?.addEventListener("click", () => this.closeGame());
        } catch (error) {
          stage.innerHTML = `<div class="fse__empty">Prediction could not be saved.<br><small>${esc(error.message)}</small></div>`;
        }
      });
    }

    gameBigList() {
      const clubs = this.directory.filter(c => c.competition_id === this.club.competition_id);
      const answers = new Map(clubs.map(c => [normalise(c.name), c.name]));
      const stage = this.stage(`
        ${this.intro("Name the division.", `You have 60 seconds to name as many clubs in ${this.club.competition_name} as you can.`)}
        <div class="fse__game-panel">
          <div class="fse__smallcaps"><span data-time>60</span> SECONDS · <span data-count>0</span> / ${clubs.length}</div>
          <div style="display:flex;gap:8px;margin-top:15px"><input class="fse__input" type="text" placeholder="Type a club…" data-entry><button type="button" class="fse__button fse__button--primary" data-add>Add</button></div>
          <div class="fse__big-list-found" data-found></div>
        </div>
      `);
      const found = new Set();
      let time = 60, ended = false;
      const timer = setInterval(() => {
        time -= 1;
        const el = qs(stage, "[data-time]");
        if (el) el.textContent = time;
        if (time <= 0) end();
      }, 1000);

      const add = () => {
        if (ended) return;
        const input = qs(stage, "[data-entry]");
        const key = normalise(input.value);
        if (answers.has(key) && !found.has(key)) {
          found.add(key);
          qs(stage, "[data-count]").textContent = found.size;
          qs(stage, "[data-found]").insertAdjacentHTML("beforeend", `<span class="fse__chip">${esc(answers.get(key))}</span>`);
        }
        input.value = "";
        input.focus();
        if (found.size === clubs.length) end();
      };
      const end = () => {
        if (ended) return;
        ended = true;
        clearInterval(timer);
        this.finishGame("the-big-list", found.size * 10, { correct: found.size, total: clubs.length });
      };
      qs(stage, "[data-add]").addEventListener("click", add);
      qs(stage, "[data-entry]").addEventListener("keydown", event => { if (event.key === "Enter") { event.preventDefault(); add(); } });
      this.gameCleanup = () => clearInterval(timer);
      qs(stage, "[data-entry]").focus();
    }

    gameFallingFootballs() {
      const stage = this.stage(`
        ${this.intro("Keep them alive.", "Tap the falling footballs before they reach the bottom. Ten seconds.")}
        <div class="fse__game-panel">
          <div class="fse__smallcaps"><span data-time>10</span> SECONDS · SCORE <span data-score>0</span></div>
          <div class="fse__fall-field" data-field></div>
        </div>
      `);
      const field = qs(stage, "[data-field]");
      let score = 0, seconds = 10, ended = false;
      const spawn = () => {
        if (ended) return;
        const ball = document.createElement("button");
        ball.type = "button";
        ball.className = "fse__fall-ball";
        ball.textContent = "◆";
        ball.style.left = `${Math.random() * 86 + 2}%`;
        ball.style.setProperty("--duration", `${2 + Math.random() * 1.5}s`);
        ball.addEventListener("click", () => {
          score += 10;
          qs(stage, "[data-score]").textContent = score;
          ball.remove();
        });
        ball.addEventListener("animationend", () => ball.remove());
        field.appendChild(ball);
      };
      const spawnTimer = setInterval(spawn, 390);
      const clock = setInterval(() => {
        seconds -= 1;
        qs(stage, "[data-time]").textContent = seconds;
        if (seconds <= 0) {
          ended = true;
          clearInterval(spawnTimer);
          clearInterval(clock);
          setTimeout(() => this.finishGame("falling-footballs", score, { seconds: 10 }), 250);
        }
      }, 1000);
      spawn();
      this.gameCleanup = () => { ended = true; clearInterval(spawnTimer); clearInterval(clock); };
    }

    gameLongThrow() {
      const stage = this.stage(`
        ${this.intro("Distance meets accuracy.", "Set the power, then keep the aim close to centre.")}
        <div class="fse__game-panel">
          <div class="fse__range-grid">
            <div class="fse__field"><label>Power <span data-power-label>75</span>%</label><input class="fse__range" type="range" min="0" max="100" value="75" data-power></div>
            <div class="fse__field"><label>Aim <span data-aim-label>50</span>%</label><input class="fse__range" type="range" min="0" max="100" value="50" data-aim></div>
          </div>
          <div class="fse__game-actions"><button type="button" class="fse__button fse__button--primary" data-throw>Take the throw</button></div>
          <div class="fse__notice">Maximum score rewards strong power without sacrificing the centre line.</div>
        </div>
      `);
      const power = qs(stage, "[data-power]");
      const aim = qs(stage, "[data-aim]");
      power.addEventListener("input", () => qs(stage, "[data-power-label]").textContent = power.value);
      aim.addEventListener("input", () => qs(stage, "[data-aim-label]").textContent = aim.value);
      qs(stage, "[data-throw]").addEventListener("click", () => {
        const p = Number(power.value), a = Number(aim.value);
        const accuracy = Math.max(0, 100 - Math.abs(a - 50) * 2);
        const raw = Math.round((p * .55) + (accuracy * .45));
        this.finishGame("long-throw", raw, { power: p, aim: a, accuracy });
      });
    }

    gameMatchLog() {
      const today = new Date().toISOString().slice(0, 10);
      const m = this.currentMatchup;
      const opponent = m ? (m.home_club_id === this.club.id ? m.away_club : m.home_club) : "";
      const homeAway = m ? (m.home_club_id === this.club.id ? "home" : "away") : "home";
      const stage = this.stage(`
        ${this.intro("Build your supporter record.", "Log the match, the away day and the miles. Verified entries can be layered in later without changing your history.")}
        <div class="fse__game-panel">
          <div class="fse__log-grid">
            <div class="fse__field"><label>Date</label><input class="fse__input" type="date" value="${today}" data-date></div>
            <div class="fse__field"><label>Opponent</label><input class="fse__input" type="text" value="${esc(opponent)}" placeholder="Opponent" data-opponent></div>
            <div class="fse__field"><label>Home / Away</label><select class="fse__select" data-ha><option value="home" ${homeAway==="home"?"selected":""}>Home</option><option value="away" ${homeAway==="away"?"selected":""}>Away</option><option value="neutral">Neutral</option></select></div>
            <div class="fse__field"><label>Miles travelled</label><input class="fse__input" type="number" min="0" max="30000" step=".1" value="0" data-miles></div>
            <div class="fse__field fse__field--full"><label>Notes</label><input class="fse__input" type="text" placeholder="Terrace, pub, train, moment…" data-notes></div>
          </div>
          <div class="fse__game-actions"><button type="button" class="fse__button fse__button--primary" data-save>Log match</button></div>
        </div>
      `);
      qs(stage, "[data-save]").addEventListener("click", async () => {
        if (!this.session?.user?.id) {
          stage.innerHTML = '<div class="fse__empty">Sign in to add matches to your permanent FanScore record.</div>';
          return;
        }
        try {
          await this.api("fse_match_logs", {
            method: "POST",
            auth: true,
            headers: { Prefer: "return=minimal" },
            body: {
              user_id: this.session.user.id,
              club_id: this.club.id,
              fixture_id: m?.fixture_id || null,
              opponent_name: qs(stage, "[data-opponent]").value.trim() || null,
              match_date: qs(stage, "[data-date]").value,
              home_away: qs(stage, "[data-ha]").value,
              venue_name: m?.venue_name || null,
              miles_travelled: clamp(qs(stage, "[data-miles]").value, 0, 30000),
              notes: qs(stage, "[data-notes]").value.trim() || null
            }
          });
          await this.loadPersonalStats();
          stage.innerHTML = '<div class="fse__result"><strong>✓</strong><p>Match added to your supporter record.</p><div class="fse__game-actions" style="justify-content:center"><button type="button" class="fse__button fse__button--quiet" data-close>Back to FanScore</button></div></div>';
          qs(stage, "[data-close]")?.addEventListener("click", () => this.closeGame());
        } catch (error) {
          stage.innerHTML = `<div class="fse__empty">Match could not be logged.<br><small>${esc(error.message)}</small></div>`;
        }
      });
    }

    gameUnavailable(key) {
      this.stage(`${this.intro("Game", "This game is registered in the English Football catalogue but does not have a homepage module yet.")}<div class="fse__empty">${esc(key)}</div>`);
    }
  }

  const boot = () => {
    document.querySelectorAll("[data-fse-root]").forEach(root => {
      if (root.dataset.fseBooted === "true") return;
      root.dataset.fseBooted = "true";
      new FanScoreEnglish(root).init();
    });
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();

  document.addEventListener("shopify:section:load", boot);
})();
